import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Plus, RefreshCw, Upload } from 'lucide-react'
import {
  Button,
  EmptyState,
  QueryErrorState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import {
  SopTable,
  useCreateSop,
  useImportSop,
  useSopCategories,
  useSops,
  type SopListQuery,
  type SopRow,
  type SopStatus,
} from '@/shell/services/sop-registry'

type StatusFilter = 'all' | SopStatus

/**
 * SOP Management — authoring surface (admin + operations only). Create,
 * import, edit, publish, and archive SOPs. Read-only browsing for every
 * other role lives on the separate /sop-library page.
 */
export function SopManagementPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [needsWork, setNeedsWork] = useState(false)
  const navigate = useNavigate()
  const searchRef = useRef<HTMLInputElement>(null)
  const pdfInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const query = useMemo<SopListQuery>(
    () => ({
      q: debouncedSearch || undefined,
      category: category || undefined,
      status: status === 'all' ? undefined : status,
      limit: 200,
    }),
    [debouncedSearch, category, status],
  )

  const { data, isLoading, isFetching, isError, error, refetch } = useSops(query)
  const { data: categoriesData } = useSopCategories()

  const createSop = useCreateSop()
  const importSop = useImportSop()

  const allRows = useMemo<SopRow[]>(() => data?.items ?? [], [data])
  const rows = useMemo(() => (needsWork ? allRows.filter((r) => r.gaps.length) : allRows), [allRows, needsWork])
  const categories = categoriesData?.categories ?? []
  const openSop = (id: string) => navigate({ to: '/sop-manage/$sopId', params: { sopId: id } })
  const editSop = (id: string) => navigate({ to: '/sop-manage/$sopId/edit', params: { sopId: id } })

  function openCreate() {
    createSop.mutate(
      { title: 'Untitled SOP', category: category || categories[0] || 'Warehouse', tags: [] },
      {
        onSuccess: (sop) => editSop(sop.id),
        onError: (err) => toast.danger(`Couldn't create the SOP${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  function uploadPdf(file: File) {
    importSop.mutate(
      {
        file,
        title: file.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').trim() || 'Untitled SOP',
        category: category || categories[0] || 'Warehouse',
        tags: [],
      },
      {
        onSuccess: (sop) => editSop(sop.id),
        onError: (err) => toast.danger(`Upload failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const draftCount = allRows.filter((r) => r.status === 'draft').length
  const publishedCount = allRows.filter((r) => r.status === 'published').length
  const needsWorkCount = allRows.filter((r) => r.gaps.length).length

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="SOP Management"
        description="Author, import, publish, and archive Standard Operating Procedures."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Drafts" value={draftCount.toLocaleString()} />
          <MetricCard variant="inline" label="Published" value={publishedCount.toLocaleString()} />
          <MetricCard variant="inline" label="To complete" value={needsWorkCount.toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search SOP title, tags, or content…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
          <Button variant="secondary" loading={importSop.isPending} onClick={() => pdfInput.current?.click()}>
            <Upload size={14} /> Upload PDF
          </Button>
          <input
            ref={pdfInput}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            aria-label="Upload a PDF SOP"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) uploadPdf(f)
              e.target.value = ''
            }}
          />
          <Button variant="primary" loading={createSop.isPending} onClick={openCreate}>
            <Plus size={14} /> New SOP
          </Button>
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <Select
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Select>
            <Select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Template"
              value={needsWork ? 'gaps' : 'all'}
              onChange={(e) => setNeedsWork(e.target.value === 'gaps')}
            >
              <option value="all">Complete and incomplete</option>
              <option value="gaps">Missing template items</option>
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>{rows.length.toLocaleString()} SOPs</ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="SOPs"
            />
          ) : rows.length === 0 && !isLoading ? (
            <EmptyState
              variant="no-match"
              title="No SOPs match"
              description="Try clearing a filter, or create the first one."
              action={
                <Button variant="secondary" onClick={openCreate}>
                  <Plus size={14} /> New SOP
                </Button>
              }
            />
          ) : (
            <SopTable
              rows={rows}
              onSelect={(row) => openSop(row.id)}
              showStatus
              showGaps
              moduleId="sop-management"
            />
          )}
        </div>
      </div>

    </div>
  )
}
